<template>
    <div class="landing-page-submitted-modal">
        <GoalModal
            :is-open="isOpen"
            :action-button-label="$t('campaign.landingPage.submitted.actionButtonLabel')"
            :summary-text="$t('campaign.landingPage.submitted.summaryText')"
            :title="$t('campaign.landingPage.submitted.title')"
            @close-modal="closeModal"
            @update-config="closeModal"
        >
            <template v-if="isConfigured" #goalParameters>
                <div class="configuration-section">
                    <div class="configuration-section-header">
                        <div class="configuration-section-label">
                            {{ $t('campaign.landingPage.submitted.urlLabel') }}
                        </div>
                    </div>
                    <div class="configuration-section-text">
                        {{ $t('campaign.landingPage.submitted.urlText') }}
                    </div>
                    <div class="configuration-url-section-body">
                        <DsInputField v-model="landingPageConfiguration.landingPageUrl" readonly />
                        <DsOutlineButton
                            class="copy-button"
                            :disabled="loading"
                            @click="copyUrl"
                        >
                            <template #leading>
                                <DsIcon name="copy" />
                            </template>
                            <template #default>
                                {{ $t('campaign.landingPage.submitted.copyLink') }}
                            </template>
                        </DsOutlineButton>
                    </div>
                </div>
                <div class="configuration-section">
                    <div class="configuration-section-header">
                        <div class="configuration-section-label">
                            {{ $t('campaign.landingPage.submitted.codeLabel') }}
                        </div>
                    </div>
                    <div class="configuration-section-text">
                        {{ $t('campaign.landingPage.submitted.codeText') }}
                    </div>
                    <div class="configuration-section-body">
                        <DsTextAreaField
                            v-model="landingPageConfiguration.landingPageHtml"
                            readonly
                            monospace
                            class="embed-code"
                        />
                        <DsOutlineButton
                            class="copy-button"
                            :disabled="loading"
                            @click="copyCode"
                        >
                            <template #leading>
                                <DsIcon name="copy" />
                            </template>
                            <template #default>
                                {{ $t('campaign.landingPage.submitted.copyCode') }}
                            </template>
                        </DsOutlineButton>
                    </div>
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import { getLandingPageSettings } from './landing.page.submitted.api';

export default {
    components: {
        GoalModal,
    },


    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },

        appInfo: {
            type: Object,
            required: true,
        },

        cell: {
            type: Object,
            default: () => ({
                value: {},
            }),
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            landingPageConfiguration: {},
            landingPageId: '0',
            loading: false,
        };
    },

    computed: {
        isConfigured() {
            return this.landingPageId !== '0';
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        loadCellValue() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            const landingPageId = cell.value.landingPageId ?? '0';

            this.landingPageId = landingPageId.toString().replace('L', '');
        },

        async load() {
            try {
                this.loading = true;

                this.loadCellValue();

                if (this.isConfigured) {
                    this.landingPageConfiguration = await getLandingPageSettings(this.landingPageId);
                }
            } catch (error) {
                const message = `${this.$t('campaign.landingPage.submitted.errorLoadingSettings')} ${error}`;

                this.$error({ message });
            } finally {
                this.loading = false;
            }
        },

        copyUrl() {
            this.$copyText(this.landingPageConfiguration.landingPageUrl);
            this.$toast({ message: this.$t('campaign.webForm.submitted.urlCopied') });
        },

        copyCode() {
            this.$copyText(this.landingPageConfiguration.landingPageHtml);
            this.$toast({ message: this.$t('campaign.webForm.submitted.codeCopied') });
        },

        closeModal() {
            this.$emit('close-modal');
        },
    },
};

</script>

<style lang="scss" scoped>
div.landing-page-submitted-modal ::v-deep {
    div.modal-body-inner {
        padding-bottom: 0;
    }
}

.configuration-section-header {
    display:        flex;
    flex-wrap:      wrap;
    justify-items:  left;
    padding-bottom: $spacing-300;
    position: relative;
}

.configuration-url-section-body {
    display: grid;
    grid-template-columns: 75% 23%;
    border-bottom: px-to-rem(0.5) solid $color-gray-200;
    margin-bottom: $spacing-250;
}

div.configuration-url-section-body > button {
    margin-left: $spacing-125;
}

.configuration-section-label {
    font-size: $font-size-xs;
    color:     $color-ink-800;
}

.configuration-section-text {
    margin-bottom: $spacing-250;
}

button.button.copy-button.outline.with-leading {
    margin-bottom: $spacing-300;
}

.embed-code {
    width: px-to-rem(580);
}
</style>
